import React, { useState } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../components/ui/card';
import { cn } from '../../lib/utils';
import { Percent, Shield, TrendingUp, Settings, ShieldAlert } from 'lucide-react';

// Import components
import { FeesConfig } from './FeesConfig';
import { LimitsConfig } from './LimitsConfig';
import { ExchangeRate } from './ExchangeRate';
import { RestrictionsConfig } from './RestrictionsConfig';

type TabId = 'fees' | 'limits' | 'exchange' | 'restrictions';

interface TabItem {
    id: TabId;
    label: string;
    icon: React.ElementType;
    description: string;
}

const tabs: TabItem[] = [
    {
        id: 'fees',
        label: 'Frais Tranzaksyon',
        icon: Percent,
        description: 'Jere pousantaj frais pa tip tranzaksyon'
    },
    {
        id: 'limits',
        label: 'Limit KYC',
        icon: Shield,
        description: 'Limit tranzaksyon pa estati KYC kliyan'
    },
    {
        id: 'exchange',
        label: 'To Echanj',
        icon: TrendingUp,
        description: 'Jere to echanj USD→HTG'
    },
    {
        id: 'restrictions',
        label: 'Restriksyon',
        icon: ShieldAlert,
        description: 'Limit montant ak orè tranzaksyon'
    },
];

export default function ConfigurationPage() {
    const [activeTab, setActiveTab] = useState<TabId>('fees');

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto space-y-4 p-4 sm:p-6 lg:p-8 pt-4 sm:pt-6">
                    {/* Breadcrumb */}
                    <div className="flex items-center justify-between">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink className="capitalize">Sistèm</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="capitalize">Konfigirasyon</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>

                    {/* Header Card */}
                    <Card className="border-none shadow-sm bg-gradient-to-r from-primary-500 to-primary-600 text-white">
                        <CardHeader className="pb-3">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <Settings className="h-6 w-6" />
                                </div>
                                <div>
                                    <CardTitle className="text-xl sm:text-2xl text-white">
                                        Konfigirasyon Sistèm
                                    </CardTitle>
                                    <CardDescription className="text-white/80 text-sm">
                                        Jere paramèt konfigirasyon pou sistèm nan
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                    </Card>

                    {/* Tab Navigation - Modern pill style */}
                    <div className="bg-white rounded-xl shadow-sm border p-1.5 overflow-x-auto">
                        <div className="flex flex-col sm:flex-row gap-1.5 min-w-max sm:min-w-full">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={cn(
                                            "flex-1 flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-left min-w-[200px] sm:min-w-0",
                                            isActive
                                                ? "bg-primary-500 text-white shadow-md"
                                                : "hover:bg-slate-100 text-slate-700"
                                        )}
                                    >
                                        <div className={cn(
                                            "p-2 rounded-md shrink-0",
                                            isActive ? "bg-white/20" : "bg-slate-100"
                                        )}>
                                            <Icon className={cn(
                                                "h-4 w-4",
                                                isActive ? "text-white" : "text-primary-500"
                                            )} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className={cn(
                                                "font-medium text-sm truncate",
                                                isActive ? "text-white" : "text-slate-900"
                                            )}>
                                                {tab.label}
                                            </p>
                                            <p className={cn(
                                                "text-xs truncate hidden sm:block",
                                                isActive ? "text-white/70" : "text-slate-500"
                                            )}>
                                                {tab.description}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                        {activeTab === 'fees' && <FeesConfig />}
                        {activeTab === 'limits' && <LimitsConfig />}
                        {activeTab === 'exchange' && <ExchangeRate />}
                        {activeTab === 'restrictions' && <RestrictionsConfig />}
                    </div>
                </div>
            </div>
        </div>
    );
}
